import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;
  #layoutRefreshQueued = false;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener('click', this.#handleUpsellNavigation);
    this.addEventListener('click', this.#handleInstallmentsClick, true);
    this.addEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener('click', this.#handleUpsellNavigation);
    this.removeEventListener('click', this.#handleInstallmentsClick, true);
    this.removeEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
  }

  /**
   * Recomputes the sticky summary after a back/forward cache restore re-renders the cart items.
   *
   * On viewports at or above 990px an open drawer survives a navigation and is reopened during
   * parsing (see the restore script in `snippets/theme-drawer.liquid`), so no open event fires
   * on the way back and the measurement taken before the shopper left is the one still applied.
   * An empty drawer measured as unpinned would keep the checkout CTA scrolling away once the
   * restore fills it with line items.
   */
  #handleCartSectionRestored = () => {
    this.#scheduleLayoutRefresh();
  };

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#scheduleLayoutRefresh();
  };

  #handleInstallmentsClick = (event) => {
    const path = event.composedPath();
    const ctaIndex = path.findIndex(
      (element) => element instanceof Element && element.id === 'shopify-installments-cta'
    );
    if (ctaIndex === -1) return;

    const ctaRoot = path.slice(ctaIndex + 1).find((element) => element instanceof ShadowRoot);
    if (!ctaRoot?.host.matches('shopify-payment-terms')) return;

    this.#themeDrawer?.close();
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;
    const wasCartEmpty = this.#isCartEmpty();

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    const updatePromise = event.promise;
    if (!updatePromise) {
      requestAnimationFrame(() => {
        if (shouldAutoOpen && !sourceModal && !this.#themeDrawer?.isOpen && !this.#isCartEmpty()) {
          this.#themeDrawer?.open();
        }
        this.#scheduleLayoutRefresh();
      });
      return;
    }

    updatePromise
      .then(({ detail }) => {
        if (!shouldAutoOpen || detail?.didError) {
          this.#scheduleLayoutRefresh();
          return;
        }

        const openAndSettle = () => {
          this.#openAfterEmptyCartUpdate(wasCartEmpty);
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  /**
   * Prevents the drawer from briefly showing the empty-cart state after the
   * first item is added. The cart-items component updates in a sibling event
   * listener, so its DOM can settle a moment after the shared request resolves.
   *
   * @param {boolean} wasCartEmpty
   */
  #openAfterEmptyCartUpdate(wasCartEmpty) {
    const openAndRefresh = () => {
      requestAnimationFrame(() => {
        if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
        this.#scheduleLayoutRefresh();
      });
    };

    if (!wasCartEmpty || !this.#isCartEmpty()) {
      openAndRefresh();
      return;
    }

    let settled = false;
    let timeoutId = 0;
    const observer = new MutationObserver(() => {
      if (!this.#isCartEmpty()) finish();
    });
    const finish = () => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      window.clearTimeout(timeoutId);
      openAndRefresh();
    };

    observer.observe(this, { childList: true, subtree: true });
    if (!this.#isCartEmpty()) {
      finish();
      return;
    }

    timeoutId = window.setTimeout(finish, 1500);
  }

  #scheduleLayoutRefresh() {
    if (this.#layoutRefreshQueued) return;
    this.#layoutRefreshQueued = true;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#layoutRefreshQueued = false;
        if (!this.isConnected) return;

        this.#updateStickyState();
        this.querySelectorAll('[data-awk-upsell-rail]').forEach((rail) => {
          if (rail instanceof HTMLElement) this.#updateUpsellControls(rail);
        });
      });
    });
  }

  /** @param {MouseEvent} event */
  #handleUpsellNavigation = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-awk-upsell-scroll]');
    if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;

    const upsells = button.closest('.awk-cart-upsells');
    const rail = upsells?.querySelector('[data-awk-upsell-rail]');
    const card = rail?.querySelector('.awk-cart-upsell-card');
    if (!(rail instanceof HTMLElement) || !(card instanceof HTMLElement)) return;

    const gap = Number.parseFloat(getComputedStyle(rail).columnGap || '0');
    const cardStep = card.getBoundingClientRect().width + gap;
    const direction = button.dataset.direction === 'previous' ? -1 : 1;
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

    rail.scrollBy({ left: direction * cardStep * 2, behavior });
    window.setTimeout(() => this.#updateUpsellControls(rail), behavior === 'smooth' ? 420 : 0);
  };

  /** @param {HTMLElement} rail */
  #updateUpsellControls(rail) {
    const upsells = rail.closest('.awk-cart-upsells');
    if (!upsells) return;

    const previous = upsells.querySelector('[data-direction="previous"]');
    const next = upsells.querySelector('[data-direction="next"]');
    const atStart = rail.scrollLeft <= 2;
    const atEnd = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2;

    if (previous instanceof HTMLButtonElement) previous.disabled = atStart;
    if (next instanceof HTMLButtonElement) next.disabled = atEnd;
  }

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
